import React, { useMemo } from 'react';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import type { EquityPoint, StrategyTrade } from '@/services/strategyService';

// ── Equity Curve ────────────────────────────────────────────

interface EquityCurveChartProps {
  equityCurve: EquityPoint[];
  height?: number;
}

export const EquityCurveChart: React.FC<EquityCurveChartProps> = ({ equityCurve, height = 200 }) => {
  const { path, areaPath, labels, minY, maxY } = useMemo(() => {
    if (equityCurve.length === 0) return { path: '', areaPath: '', labels: [] as any[], minY: 0, maxY: 0 };

    const values = equityCurve.map(p => p.equity);
    const mn = Math.min(...values);
    const mx = Math.max(...values);
    const range = mx - mn || 1;
    const pad = range * 0.1;
    const yMin = mn - pad;
    const yMax = mx + pad;
    const yRange = yMax - yMin;

    const w = 100;
    const h = 100;

    const pts = equityCurve.map((p, i) => ({
      x: equityCurve.length === 1 ? 50 : (i / (equityCurve.length - 1)) * w,
      y: h - ((p.equity - yMin) / yRange) * h,
    }));

    const linePath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
    const area = `${linePath} L${pts[pts.length - 1].x},${h} L${pts[0].x},${h} Z`;

    // Y-axis labels (5 levels)
    const labelCount = 5;
    const lbls = Array.from({ length: labelCount }, (_, i) => {
      const v = yMin + (yRange * i) / (labelCount - 1);
      return { value: v, y: h - ((v - yMin) / yRange) * h };
    });

    return { path: linePath, areaPath: area, labels: lbls, minY: yMin, maxY: yMax };
  }, [equityCurve]);

  if (equityCurve.length === 0) return null;

  const lastEquity = equityCurve[equityCurve.length - 1].equity;
  const firstEquity = equityCurve[0].equity;
  const isPositive = lastEquity >= firstEquity;

  return (
    <Card>
      <CardHeader className="p-3 pb-1">
        <CardTitle className="text-sm">Equity Curve</CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-1">
        <div style={{ height }} className="relative">
          <svg viewBox="-12 -2 114 104" className="w-full h-full" preserveAspectRatio="none">
            {/* Grid lines */}
            {labels.map((l, i) => (
              <line key={i} x1="0" x2="100" y1={l.y} y2={l.y}
                stroke="currentColor" strokeOpacity="0.07" strokeWidth="0.3" />
            ))}
            {/* Area fill */}
            <path d={areaPath} fill={isPositive ? 'rgb(34 197 94 / 0.15)' : 'rgb(239 68 68 / 0.15)'} />
            {/* Line */}
            <path d={path} fill="none" stroke={isPositive ? '#22c55e' : '#ef4444'} strokeWidth="0.8" />
          </svg>
          {/* Y labels */}
          <div className="absolute top-0 left-0 h-full flex flex-col justify-between text-[9px] text-muted-foreground pointer-events-none" style={{ width: 48 }}>
            {[...labels].reverse().map((l, i) => (
              <span key={i}>${l.value >= 1000 ? `${(l.value / 1000).toFixed(1)}k` : l.value.toFixed(0)}</span>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ── P&L Distribution Histogram ──────────────────────────────

interface PnLDistributionChartProps {
  trades: StrategyTrade[];
  height?: number;
}

export const PnLDistributionChart: React.FC<PnLDistributionChartProps> = ({ trades, height = 180 }) => {
  const { bins, maxCount } = useMemo(() => {
    const pnls = trades.map(t => t.realizedPnL).filter(p => p !== 0);
    if (pnls.length === 0) return { bins: [], maxCount: 0 };

    const min = Math.min(...pnls);
    const max = Math.max(...pnls);
    const range = max - min || 1;
    const binCount = Math.min(20, Math.max(5, Math.ceil(pnls.length / 3)));
    const binWidth = range / binCount;

    const buckets: { low: number; high: number; count: number; isPositive: boolean }[] = [];
    for (let i = 0; i < binCount; i++) {
      const low = min + i * binWidth;
      const high = low + binWidth;
      buckets.push({
        low,
        high,
        count: pnls.filter(p => p >= low && (i === binCount - 1 ? p <= high : p < high)).length,
        isPositive: (low + high) / 2 >= 0,
      });
    }

    return { bins: buckets, maxCount: Math.max(...buckets.map(b => b.count), 1) };
  }, [trades]);

  if (bins.length === 0) return null;

  return (
    <Card>
      <CardHeader className="p-3 pb-1">
        <CardTitle className="text-sm">P&L Distribution</CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-1">
        <div style={{ height }} className="flex items-end gap-[1px]">
          {bins.map((bin, i) => {
            const pct = (bin.count / maxCount) * 100;
            return (
              <div
                key={i}
                className="flex-1 relative group"
                style={{ height: '100%' }}
              >
                <div
                  className={`absolute bottom-0 left-0 right-0 rounded-t-sm transition-all ${bin.isPositive ? 'bg-green-500/70' : 'bg-red-500/70'}`}
                  style={{ height: `${Math.max(pct, 2)}%` }}
                />
                {/* Tooltip */}
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 hidden group-hover:block bg-popover border rounded px-1.5 py-0.5 text-[9px] whitespace-nowrap z-10 shadow-md">
                  ${bin.low.toFixed(0)} – ${bin.high.toFixed(0)}: {bin.count}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex justify-between text-[9px] text-muted-foreground mt-1">
          <span>${bins[0].low.toFixed(0)}</span>
          <span>P&L ($)</span>
          <span>${bins[bins.length - 1].high.toFixed(0)}</span>
        </div>
      </CardContent>
    </Card>
  );
};

// ── MFE / MAE Scatter Plot ──────────────────────────────────

interface MfeScatterPlotProps {
  trades: StrategyTrade[];
  height?: number;
}

export const MfeScatterPlot: React.FC<MfeScatterPlotProps> = ({ trades, height = 180 }) => {
  const { points, maxVal } = useMemo(() => {
    const pts = trades
      .filter(t => t.maxFavorableExcursion > 0 || t.maxAdverseExcursion > 0)
      .map(t => ({
        mfe: t.maxFavorableExcursion,
        mae: t.maxAdverseExcursion,
        pnl: t.realizedPnL,
        symbol: t.symbol,
      }));
    if (pts.length === 0) return { points: pts, maxVal: 1 };

    const maxMfe = Math.max(...pts.map(p => p.mfe));
    const maxMae = Math.max(...pts.map(p => p.mae));
    return { points: pts, maxVal: Math.max(maxMfe, maxMae, 0.01) };
  }, [trades]);

  if (points.length === 0) return null;

  const w = 100;
  const h = 100;

  return (
    <Card>
      <CardHeader className="p-3 pb-1">
        <CardTitle className="text-sm">MFE vs MAE</CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-1">
        <div style={{ height }} className="relative">
          <svg viewBox="-8 -2 112 108" className="w-full h-full">
            {/* Grid */}
            <line x1="0" x2={w} y1={h} y2={h} stroke="currentColor" strokeOpacity="0.15" strokeWidth="0.3" />
            <line x1="0" x2="0" y1="0" y2={h} stroke="currentColor" strokeOpacity="0.15" strokeWidth="0.3" />
            {/* Diagonal guide (MFE = MAE) */}
            <line x1="0" x2={w} y1={h} y2="0" stroke="currentColor" strokeOpacity="0.1" strokeWidth="0.3" strokeDasharray="2,2" />
            {/* Points */}
            {points.map((p, i) => {
              const cx = (p.mae / maxVal) * w;
              const cy = h - (p.mfe / maxVal) * h;
              const color = p.pnl >= 0 ? '#22c55e' : '#ef4444';
              return (
                <g key={i}>
                  <circle cx={cx} cy={cy} r="1.5" fill={color} opacity={0.7} />
                  {/* Hover area */}
                  <circle cx={cx} cy={cy} r="3" fill="transparent" className="cursor-pointer">
                    <title>{p.symbol}: MFE ${p.mfe.toFixed(2)}, MAE ${p.mae.toFixed(2)}, P&L ${p.pnl.toFixed(2)}</title>
                  </circle>
                </g>
              );
            })}
          </svg>
          {/* Axis labels */}
          <div className="absolute bottom-0 left-0 right-0 text-center text-[9px] text-muted-foreground">
            MAE ($) →
          </div>
          <div className="absolute top-1/2 -left-1 -translate-y-1/2 -rotate-90 text-[9px] text-muted-foreground whitespace-nowrap origin-center">
            ← MFE ($)
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
